import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "lucide-react";
import { format } from "date-fns";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { isArtistProfileVisible } from "@/lib/artistVisibility";
import ExternalNewsLink from "@/components/ExternalNewsLink";
import ValueProposition from "@/components/ValueProposition";
import { ik, ikSrcSet } from "@/lib/imageOptimization";

// Hero banner URL from storage for ImageKit optimization
const HERO_BANNER_URL = "https://hxpzfabkvxperzpvjoys.supabase.co/storage/v1/object/public/artist-images/marketing/hero-banner.png";
interface NewsArticle {
  id: string;
  title: string;
  summary: string | null;
  link: string;
  publication_date: string | null;
  source: string;
  image_url: string | null;
}

// Safely decode HTML entities
const decodeHtmlEntities = (text: string): string => {
  if (!text) return '';
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.documentElement.textContent || '';
};
const LandingPage = () => {
  const {
    user,
    isLoading: authLoading
  } = useAuth();
  const isMobile = useIsMobile();
  const isSignedIn = !authLoading && !!user;
  const showDesktopHeroBanner = !authLoading && !user && !isMobile;

  // Fetch featured artists
  const {
    data: allArtists,
    isLoading: artistsLoading
  } = useQuery({
    queryKey: ["featured-artists"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("bands").select(`
          id,
          name,
          slug,
          genre,
          selected_image,
          featured,
          bio,
          spotify_id,
          bandcamp_url,
          bandcamp_embed
        `).eq("featured", true).eq("entity_type", "artist").limit(20);
      if (error) throw error;
      return data;
    }
  });

  // Fetch latest news articles
  const {
    data: newsArticles,
    isLoading: newsLoading
  } = useQuery({
    queryKey: ["latest-news"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("news_articles").select("id, title, summary, link, publication_date, source, image_url").order("publication_date", {
        ascending: false
      }).limit(5);
      if (error) throw error;
      return data as NewsArticle[];
    }
  });

  // Filter to only show visible profiles, limit to 8
  const featuredArtists = useMemo(() => {
    if (!allArtists) return [];
    return allArtists.filter(isArtistProfileVisible).slice(0, 8);
  }, [allArtists]);
  return <>
      <Helmet>
        <title>Bortal — The Home for Emerging Artists</title>
        <meta name="description" content="Bortal is a new platform for emerging artists to showcase their identity, connect with fans, and build community." />
        <meta name="keywords" content="emerging artists, music discovery, artist identity, fan communities, independent music, indie artists, music platform" />
        <link rel="canonical" href="https://bortalapp.com/" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://bortalapp.com/" />
        <meta property="og:title" content="Bortal — The Home for Emerging Artists" />
        <meta property="og:description" content="Bortal is a new platform for emerging artists to showcase their identity, connect with fans, and build community." />
        <meta property="og:image" content="https://bortalapp.com/og-image.png" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bortal — The Home for Emerging Artists" />
        <meta name="twitter:description" content="Bortal is a new platform for emerging artists to showcase their identity, connect with fans, and build community." />
        <meta name="twitter:image" content="https://bortalapp.com/og-image.png" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        {/* Universal Navbar */}
        <Navbar />
        
        {/* Hero section for non-signed-in users OR spacer for signed-in */}
        {isSignedIn ? <div className="pt-20" /> // Spacer for fixed navbar
      : showDesktopHeroBanner ?
      // Desktop hero banner with overlay CTA (ImageKit optimized)
      <section className="pt-16 md:pt-[20px]">
            <div className="relative w-full my-[20px]">
              <img src={ik(HERO_BANNER_URL, 1920, 640)} srcSet={ikSrcSet(HERO_BANNER_URL, [1024, 1440, 1920])} sizes="100vw" alt="Illustration of a band performing together - A home for emerging artists and the fans who champion them" className="w-full h-auto object-cover" width={1920} height={640} fetchPriority="high" />
              {/* Overlay CTA Button - left edge aligned with tagline text (approx 52% from left) */}
              <div className="absolute bottom-[10%] left-[52%] lg:bottom-[12%]">
                <Button asChild size="xl" className="bg-[hsl(40,45%,75%)] hover:bg-[hsl(40,45%,68%)] text-[hsl(0,0%,15%)] font-semibold text-lg lg:text-xl px-8 py-4 lg:px-10 lg:py-5 rounded-lg shadow-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[hsl(40,45%,75%)]" aria-label="Start discovering emerging artists on Bortal">
                  <Link to="/artists">
                    Start discovering
                  </Link>
                </Button>
              </div>
            </div>
          </section> :
      // Mobile hero - just the image, no overlay
      <section className="w-full pt-16">
            <img src={ik(HERO_BANNER_URL, 768, 512)} srcSet={ikSrcSet(HERO_BANNER_URL, [375, 480, 640, 768])} sizes="100vw" alt="Illustration of a band performing together - A home for emerging artists and the fans who champion them" className="w-full h-auto object-cover" width={768} height={512} fetchPriority="high" />
          </section>}

        {/* Value Proposition */}
        <ValueProposition />

        {/* Main content */}
        <main className="flex-1">
          {/* Section 2: Featured Artists */}
          <section className="container mx-auto px-6 py-12 pb-0 pt-[20px]">
            {/* Section header */}
            <div className="flex items-end justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground my-0 mt-[20px]">Touring Now

            </h2>
              <Link to="/artists" className="text-sm text-primary hover:text-primary/80 transition-colors font-medium hidden md:block">
                View all artists →
              </Link>
            </div>

            {/* Artists grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {artistsLoading ?
            // Skeleton loading state
            Array.from({
              length: 8
            }).map((_, i) => <Skeleton key={i} className="aspect-square w-full rounded-lg" />) : featuredArtists.map((artist, index) => {
              const isLCP = index === 0;
              const optimizedSrc = ik(artist.selected_image, 304, 304);
              const srcSet = ikSrcSet(artist.selected_image, [152, 228, 304, 456], 1);
              return <Link to={`/artists/${artist.slug}`} key={artist.id} className="block relative pb-4">
                      {/* Square image container with overlay */}
                      <div className="aspect-square overflow-hidden bg-muted relative rounded-lg">
                        {artist.selected_image ? <img src={optimizedSrc || artist.selected_image} srcSet={srcSet || undefined} sizes="(min-width: 768px) 25vw, 50vw" alt={artist.name} width={304} height={304} loading={isLCP ? "eager" : "lazy"} decoding="async" fetchPriority={isLCP ? "high" : undefined} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center bg-muted">
                            <span className="text-4xl font-bold text-muted-foreground/50">
                              {artist.name.charAt(0).toUpperCase()}
                            </span>
                          </div>}
                        
                        {/* Bottom-up black gradient overlay */}
                        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none" />
                        
                        {/* Artist name - positioned in gradient area, wraps to 2 lines */}
                        <h3 className="absolute bottom-3 left-3 right-3 font-bold text-white text-lg md:text-xl leading-tight line-clamp-2 drop-shadow-md">
                          {artist.name}
                        </h3>
                      </div>
                      
                      {/* Floating genre tag - straddles bottom edge of image (50% on image, 50% below) */}
                      {artist.genre?.[0] && <span className="absolute bottom-4 right-2 px-1.5 py-0.5 text-[10px] font-medium text-black bg-white border-2 translate-y-1/2" style={{
                  borderColor: '#6f9099'
                }}>
                          {artist.genre[0]}
                        </span>}
                    </Link>;
            })}
            </div>

            {/* Mobile "View all" link */}
            <div className="text-center mt-8 md:hidden">
              <Link to="/artists" className="text-sm text-primary hover:text-primary/80 transition-colors font-medium">
                View all artists →
              </Link>
            </div>
          </section>

          {/* Section 3: Latest News */}
          <section className="container mx-auto px-6 py-12 border-t border-border pt-[35px] my-[30px] pb-0">
            {/* Section header */}
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground">
                Latest News
              </h2>
              <Link to="/news" className="text-sm text-primary hover:text-primary/80 transition-colors font-medium">
                View all news →
              </Link>
            </div>

            {/* News list */}
            <div className="space-y-4">
              {newsLoading ?
            // Skeleton loading state
            Array.from({
              length: 5
            }).map((_, i) => <div key={i} className="flex gap-4 p-4 border border-border rounded-lg">
                    <Skeleton className="w-24 h-24 flex-shrink-0 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-5 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>) : newsArticles?.length === 0 ? <div className="text-center py-8 text-muted-foreground border border-dashed border-border rounded-lg">
                  No news articles yet. Check back soon!
                </div> : newsArticles?.map((article) => {
              const rawImageUrl = article.image_url && article.image_url !== '/placeholder.svg' ? article.image_url : null;
              const optimizedImageUrl = rawImageUrl ? ik(rawImageUrl, 96, 96) : null;
              return <ExternalNewsLink href={article.link} key={article.id} className="group flex gap-4 p-4 border border-border rounded-lg hover:border-primary/30 hover:bg-muted/20 transition-all">
                      {/* Thumbnail */}
                      {rawImageUrl && <div className="w-24 h-24 flex-shrink-0 overflow-hidden bg-muted">
                          <img src={optimizedImageUrl || rawImageUrl} alt="" width={96} height={96} className="w-full h-full object-cover" loading="lazy" decoding="async" onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.parentElement!.style.display = 'none';
                  }} />
                        </div>}
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Source + Date */}
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="secondary" className="rounded-none text-xs font-medium">
                            {article.source}
                          </Badge>
                          {article.publication_date && <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(article.publication_date), "MMM d, yyyy")}
                            </span>}
                        </div>
                        
                        {/* Headline - clickable with hover underline */}
                        <h3 className="font-semibold text-foreground text-sm leading-snug line-clamp-1 mb-1 group-hover:text-primary group-hover:underline underline-offset-2 transition-colors">
                          {decodeHtmlEntities(article.title)}
                        </h3>
                        
                        {/* Summary - max 2 lines */}
                        {article.summary && <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                            {decodeHtmlEntities(article.summary)}
                          </p>}
                      </div>
                    </ExternalNewsLink>;
            })}
            </div>

            {/* Mobile "View all" link */}
            <div className="text-center mt-8 md:hidden">
              <Link to="/news" className="text-sm text-primary hover:text-primary/80 transition-colors font-medium">
                View all news →
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>;
};
export default LandingPage;